import { Title, Flex, Tabs } from '@mantine/core';
import { CompanyHeader } from '@features/company';
import { CompanyInfoAbout } from './company-info-about';
import { CompanyInfoVacancies } from './company-info-vacancies';
import { EmployeList } from '@widgets/employe';
import { CompanyRating, CompanyReviews } from '@widgets/company';
import { useState } from 'react';
import { useGetCompaniesTypes, type Company } from '@entities/company';
import { CompanyUpdate } from '../company-update';
import { joinStringWithFallback } from '@shared/helpers';
import { useFeatureFlag } from '@packages/feature-flags/react';

type CompanyInfoProps = {
  company: Company;
};

export const CompanyInfo = ({ company }: CompanyInfoProps) => {
  const [isEditCompany, setIsEditCompany] = useState(false);
  const { data: companyTypes } = useGetCompaniesTypes();
  const ratingsAndReviewsEnabled = useFeatureFlag('ratingsAndReviews');

  return (
    <Flex wrap='wrap' direction='column' gap={32} mx='auto' maw={1512} w='100%'>
      <CompanyHeader
        id={company.id}
        name={company.name}
        createdAt={company.createdAt}
        updatedAt={company.updatedAt}
        isActive={company.isActive}
      />

      <Tabs defaultValue='aboutCompany' variant='pills' maw='100%'>
        <Flex gap={32} direction='column'>
          <Tabs.List>
            <Tabs.Tab value='aboutCompany'>О компании</Tabs.Tab>
            <Tabs.Tab value='vacanciesCompany'>Вакансии компании</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='aboutCompany'>
            <Flex gap={24} align='flex-start'>
              {isEditCompany ? (
                <CompanyUpdate
                  initialValues={{
                    name: company.name,
                    type: company.type,
                    nif: company.nif,
                    city: company?.city?.id?.toString() ?? '',
                    economicActivitiesIds: company.economicActivities.map(
                      activity => activity.id.toString()
                    ),
                    manager: company.manager.id.toString(),
                    description: company.description,
                  }}
                  initialNccVerified={company.nccVerified}
                  handleBack={() => setIsEditCompany(false)}
                />
              ) : (
                <CompanyInfoAbout
                  companyName={company.name}
                  companyType={
                    companyTypes?.find(type => type.value === company.type)
                      ?.title ?? '-'
                  }
                  companyNif={company.nif}
                  companyNccVerified={company.nccVerified}
                  companyCity={company?.city?.name ?? '-'}
                  companyManager={joinStringWithFallback(
                    [
                      company.manager.profile.firstName,
                      company.manager.profile.lastName,
                    ],
                    { fallback: '-' }
                  )}
                  companyEconomicActivities={
                    company.economicActivities
                      .map(activity => activity.name)
                      .join(', ') || '-'
                  }
                  companyDescription={company.description || '-'}
                  handleEdit={() => setIsEditCompany(true)}
                />
              )}

              <Flex direction='column' gap={16}>
                {ratingsAndReviewsEnabled && (
                  <CompanyRating
                    rating={company.rating}
                    level={company.level}
                  />
                )}
                <EmployeList />
                {ratingsAndReviewsEnabled && (
                  <CompanyReviews companyId={company.id} />
                )}
              </Flex>
            </Flex>
          </Tabs.Panel>

          <Tabs.Panel value='vacanciesCompany'>
            <Flex gap={32} direction='column'>
              <Title order={4}>Вакансии компании</Title>

              <CompanyInfoVacancies companyId={company.id} />
            </Flex>
          </Tabs.Panel>
        </Flex>
      </Tabs>
    </Flex>
  );
};
